/**
 * Operator presence. Replaces the old `usePing` probe (FLY3D-1029).
 *
 * The old approach published to `ping/{userId}` and treated 5 seconds of
 * silence as "not online". It reported logged-on pilots as offline, because a
 * pong only arrives if the target browser happens to hold a live subscription
 * at that instant, and the dashboard tore its MQTT client down on every device
 * switch. A probe cannot distinguish "they are not there" from "we did not
 * manage to ask".
 *
 * Presence is now READ from two sources, never probed:
 *
 * - **Retained MQTT state** on `presence/{org}/{userId}`, published by each
 *   dashboard on connect and by the broker's Last Will if a browser dies
 *   ungracefully. Because it is retained, subscribing to `presence/{org}/+`
 *   delivers the current state of every colleague immediately, with no
 *   round trip and no timeout.
 * - **The user-presence API**, which is derived from AWS IoT lifecycle events
 *   plus heartbeats server-side. This is the authoritative, graded read used
 *   at the decision point, and it works even when our own MQTT is down.
 *
 * This composable also publishes this browser's own heartbeat.
 */
import { computed, getCurrentScope, onScopeDispose, ref } from 'vue'
import { storeToRefs } from 'pinia'

import { useApi } from '@/config/apiFetcher'
import { mqttStore } from '@/store/mqtt'
import { appStore } from '@/store/app'
import { useUserStore } from '@/store/userStore'
import log from '@/utils/useLogger'
import {
  CONFIRMED_ABSENT_STATES,
  HEARTBEAT_INTERVAL_MS,
  REACHABLE_STATES,
  type PresenceState,
  type UserPresence,
} from '@/constants/presence'

// Re-exported so callers can keep importing presence types and helpers from
// one place. The definitions live in constants/presence.ts because they are
// pure and must be unit testable without MQTT or Pinia.
export {
  describeAircraftEvidence,
  describeDisconnectReason,
  formatLastSeen,
  humanisePresence,
} from '@/constants/presence'
export type { PresenceState, UserPresence } from '@/constants/presence'

// Module scope so every caller shares one map and one heartbeat timer.
const presenceByUserId = ref<Map<string, UserPresence>>(new Map())
let heartbeatTimer: number | null = null
let lastInputAt = Date.now()
let inputListenersAttached = false

const INPUT_EVENTS = ['pointerdown', 'keydown', 'wheel', 'mousemove'] as const

function noteInput() {
  lastInputAt = Date.now()
}

/**
 * Track when the operator last touched the machine, so a dashboard that is
 * merely open can be told apart from one someone is actually flying from.
 * `mousemove` is passive and throttled by only writing a timestamp.
 */
function attachInputListeners() {
  if (inputListenersAttached || typeof window === 'undefined') return
  INPUT_EVENTS.forEach((event) => window.addEventListener(event, noteInput, { passive: true }))
  inputListenersAttached = true
}

function detachInputListeners() {
  if (!inputListenersAttached || typeof window === 'undefined') return
  INPUT_EVENTS.forEach((event) => window.removeEventListener(event, noteInput))
  inputListenersAttached = false
}

export default () => {
  const api = useApi()
  const mqttInstance = mqttStore()
  const { organisationId, userId } = storeToRefs(useUserStore())
  const { telemetry, deviceGroupIDs } = storeToRefs(appStore())
  const { sessionState, realtimeDegraded } = storeToRefs(mqttInstance)

  /** True when our own real-time link is healthy enough to trust MQTT state. */
  const sessionHealthy = computed(() => sessionState.value === 'connected')

  /**
   * Apply a retained or live presence message from `presence/{org}/+`.
   *
   * MQTT only tells us online or offline. It cannot tell us `active` versus
   * `idle`, because heartbeats are not broadcast to peers (that would put
   * every operator's beat on every other operator's socket). So an MQTT
   * `online` lands as `idle`: connected and reachable, activity unknown. The
   * API is what refines it at the decision point.
   */
  const applyPresenceMessage = (topic: string, raw: string) => {
    try {
      const segments = topic.split('/')
      // presence/{org}/{userId}
      if (segments.length !== 3) return
      const targetUserId = segments[2]
      const payload = raw ? JSON.parse(raw) : {}

      const next: UserPresence = {
        userID: targetUserId,
        state: payload.state === 'online' ? 'idle' : 'offline',
        lastSeenAt: payload.at || new Date().toISOString(),
        disconnectReason: payload.source === 'lwt' ? 'CONNECTION_LOST' : null,
      }

      const merged = new Map(presenceByUserId.value)
      merged.set(targetUserId, { ...merged.get(targetUserId), ...next })
      presenceByUserId.value = merged
      log.debug(`presence via MQTT: ${targetUserId} -> ${next.state}`)
    } catch (error) {
      log.error('Error handling presence message:', error)
    }
  }

  /** Last known state from MQTT. Synchronous, no network. */
  const getPresence = (targetUserId: string): UserPresence => {
    return (
      presenceByUserId.value.get(targetUserId) || {
        userID: targetUserId,
        state: 'unknown',
      }
    )
  }

  /**
   * Authoritative graded read. Used at the takeover decision point.
   *
   * Falls back to the retained MQTT state when the API is unavailable, which
   * makes this work before the user-presence service is deployed: retained
   * state plus the broker's Last Will is already real disconnect evidence,
   * just without the `active` versus `idle` distinction.
   *
   * Whatever happens, a failure to ask degrades to `unknown`, never to
   * `offline`. Not knowing must not be presented as a confirmed absence.
   */
  const fetchPresence = async (targetUserIds: string[]): Promise<UserPresence[]> => {
    const ids = [...new Set(targetUserIds.filter(Boolean))]
    if (!ids.length) return []

    const baseUrl = 'https://dashboard.idi-fly.com/user-presence-dev'
    // getPresence returns `unknown` for anyone we hold no retained state for.
    const fallback = (): UserPresence[] => ids.map((id) => getPresence(id))

    if (!organisationId.value) return fallback()

    try {
      const res = await api.get(`${baseUrl}/user-presence/users`, {
        params: {
          organisationID: organisationId.value,
          userIDs: ids.join(','),
        },
      })
      const results: UserPresence[] = res.data?.presence || []
      if (res.data?.notReturned?.length) {
        log.warn('presence response was capped', res.data.notReturned)
      }

      const merged = new Map(presenceByUserId.value)
      results.forEach((entry) => merged.set(entry.userID, entry))
      presenceByUserId.value = merged

      return ids.map(
        (id) => results.find((entry) => entry.userID === id) || { userID: id, state: 'unknown' },
      )
    } catch (error) {
      log.error('Failed to read presence; falling back to retained MQTT state', error)
      return fallback()
    }
  }

  /** Single-user convenience wrapper around fetchPresence. */
  const fetchUserPresence = async (targetUserId: string): Promise<UserPresence> => {
    const [presence] = await fetchPresence([targetUserId])
    return presence || { userID: targetUserId, state: 'unknown' }
  }

  const isReachable = (state: PresenceState) => REACHABLE_STATES.includes(state)
  const isConfirmedAbsent = (state: PresenceState) => CONFIRMED_ABSENT_STATES.includes(state)

  /** Start publishing this browser's heartbeat. Called once, after login. */
  const startHeartbeat = () => {
    if (heartbeatTimer !== null) return
    attachInputListeners()

    const publish = () => {
      if (!userId.value || !organisationId.value) return
      mqttInstance.publishPresenceHeartbeat({
        inputIdleMs: Math.max(0, Date.now() - lastInputAt),
        activeDeviceGroupIds: (deviceGroupIDs.value || [])
          .map((group: any) => group?.deviceGroupID || group)
          .filter(Boolean),
        isFlying: Boolean(telemetry.value?.aircraftState?.isFlying),
        appVersion: import.meta.env.VITE_APP_VERSION,
      })
    }

    publish()
    heartbeatTimer = window.setInterval(publish, HEARTBEAT_INTERVAL_MS)
    log.info(`presence heartbeat started (${HEARTBEAT_INTERVAL_MS}ms)`)
  }

  const stopHeartbeat = () => {
    if (heartbeatTimer !== null) {
      window.clearInterval(heartbeatTimer)
      heartbeatTimer = null
    }
    detachInputListeners()
  }

  // Guarded: this composable is also called from other composables that run
  // outside a component scope, where an unguarded onScopeDispose only warns.
  if (getCurrentScope()) {
    onScopeDispose(() => {
      // Only the interval is per-scope; the shared map deliberately outlives it.
      stopHeartbeat()
    })
  }

  return {
    presenceByUserId,
    sessionHealthy,
    realtimeDegraded,
    applyPresenceMessage,
    getPresence,
    fetchPresence,
    fetchUserPresence,
    isReachable,
    isConfirmedAbsent,
    startHeartbeat,
    stopHeartbeat,
  }
}
